/**
 * LCFPIProjectsTab.js — Projects Tab
 * Wraps: Submit Project, Assessment, Finance Readiness, Eco-Labels,
 *        PDD Builder, EOI Generator, Project Health, Workflow
 * Six sub-sections in one clean workspace.
 */
import React, { useState, Suspense, lazy } from "react";
import { PlusCircle, ClipboardList, TrendingUp, Award, FileText, Send, BarChart2, Workflow } from "lucide-react";
import { LCFPIErrorBoundary } from "@/components/lcfpi/LCFPIPageShell";

const LCFPISubmitProject   = lazy(() => import("./LCFPISubmitProject"));
const LCFPIAssessment      = lazy(() => import("./LCFPIAssessment"));
const LCFPIFinanceReadiness= lazy(() => import("./LCFPIFinanceReadiness"));
const LCFPIEcoLabels       = lazy(() => import("./LCFPIEcoLabels"));
const LCFPIPDDBuilder      = lazy(() => import("./LCFPIPDDBuilder"));
const LCFPIEOIGenerator    = lazy(() => import("./LCFPIEOIGenerator"));
const LCFPIProjectHealth   = lazy(() => import("./LCFPIProjectHealth"));
const LCFPIWorkflow        = lazy(() => import("./LCFPIWorkflow"));

const SECTIONS = [
  { id: "submit",      label: "New Project",         icon: PlusCircle,   accent: "#7c3aed",  desc: "Register & intake"     },
  { id: "assessment",  label: "Assessment",          icon: ClipboardList,accent: "#1e40af",  desc: "Climate · Circularity · SDG" },
  { id: "readiness",   label: "Finance Readiness",   icon: TrendingUp,   accent: "#059669",  desc: "Bankability · DFI score" },
  { id: "eco-labels",  label: "Eco-Labels & SDGs",   icon: Award,        accent: "#d97706",  desc: "Certification pathways" },
  { id: "pdd",         label: "PDD Builder",         icon: FileText,     accent: "#0891b2",  desc: "Documentation"          },
  { id: "eoi",         label: "EOI Generator",       icon: Send,         accent: "#8b5cf6",  desc: "Investor outreach"      },
  { id: "health",      label: "Project Health",      icon: BarChart2,    accent: "#22c55e",  desc: "Portfolio monitoring"   },
  { id: "workflow",    label: "Lifecycle",           icon: Workflow,     accent: "#64748b",  desc: "Stage tracking"         },
];

const Loader = () => (
  <div className="flex items-center justify-center py-24">
    <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
  </div>
);

export default function LCFPIProjectsTab() {
  const [active, setActive] = useState("submit");
  const sec = SECTIONS.find(s => s.id === active) || SECTIONS[0];

  return (
    <div style={{ minHeight: "calc(100vh - 88px)", background: "#060d1a" }}>
      {/* Sub-tab bar */}
      <div style={{
        background: "#07101e",
        borderBottom: "1px solid #1e293b",
        padding: "0 24px",
        display: "flex", gap: 2, alignItems: "center",
        overflowX: "auto",
      }}>
        {SECTIONS.map(s => {
          const isActive = active === s.id;
          const Icon = s.icon;
          return (
            <button key={s.id}
              data-testid={`lcfpi-projects-sub-${s.id}`}
              onClick={() => setActive(s.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "10px 14px",
                background: isActive ? `${s.accent}18` : "transparent",
                border: "none",
                borderBottom: `2px solid ${isActive ? s.accent : "transparent"}`,
                color: isActive ? "#fff" : "#64748b",
                fontSize: 11, fontWeight: isActive ? 700 : 500,
                cursor: "pointer", whiteSpace: "nowrap",
                transition: "all .15s",
              }}>
              <Icon size={11} color={isActive ? s.accent : "currentColor"} />
              {s.label}
            </button>
          );
        })}
      </div>

      {/* Section description */}
      <div style={{ padding: "12px 24px", borderBottom: "1px solid #0f172a", background: "#060d1a" }}>
        <p style={{ fontSize: 10, color: "#475569", textTransform: "uppercase", letterSpacing: ".08em" }}>
          <span style={{ color: sec.accent, fontWeight: 700 }}>Projects</span> › {sec.label} · {sec.desc}
        </p>
      </div>

      {/* Content */}
      <LCFPIErrorBoundary>
        <Suspense fallback={<Loader />}>
          <div style={{ padding: "20px 24px" }}>
            {active === "submit"     && <LCFPISubmitProject />}
            {active === "assessment" && <LCFPIAssessment />}
            {active === "readiness"  && <LCFPIFinanceReadiness />}
            {active === "eco-labels" && <LCFPIEcoLabels />}
            {active === "pdd"        && <LCFPIPDDBuilder />}
            {active === "eoi"        && <LCFPIEOIGenerator />}
            {active === "health"     && <LCFPIProjectHealth />}
            {active === "workflow"   && <LCFPIWorkflow />}
          </div>
        </Suspense>
      </LCFPIErrorBoundary>
    </div>
  );
}
